import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { useForceLightMode } from '../hooks/useForceLightMode';

export default function ResetPassword() {
  usePageTitle('Recuperar Senha');
  useForceLightMode();
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/recuperar-senha/recuperar-senha', { email: email.trim().toLowerCase() });
      setSuccess(true);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError(t('reset_password.rate_limit_error'));
      } else {
        setSuccess(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen bg-background">
      <div className="flex w-full flex-col lg:flex-row">
        <div className="relative hidden flex-1 overflow-hidden lg:block">
          <div className="absolute inset-0 bg-gradient-to-br from-[#001A72] via-[#013EFB] to-[#0A7AFF]" />
        </div>

        <div className="flex flex-1 items-center justify-center px-6 py-12 sm:px-12">
          <div className="w-full max-w-md">
            <img
              src="/logo-stant.svg"
              alt="Stant"
              className="mx-auto w-44 h-auto mb-8"
            />

            <Card className="bg-white/90 shadow-xl shadow-black/5 backdrop-blur-sm">
              {success ? (
                <>
                  <CardHeader className="text-center space-y-2">
                    <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <CardTitle className="text-2xl font-bold">{t('reset_password.email_sent_title')}</CardTitle>
                    <CardDescription>
                      {t('reset_password.success_message')}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800 text-center">
                        {t('reset_password.check_inbox_instruction')}
                      </p>
                    </div>

                    <div className="text-center">
                      <Link
                        to="/login"
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ArrowLeft size={16} />
                        {t('reset_password.back_to_login')}
                      </Link>
                    </div>
                  </CardContent>
                </>
              ) : (
                <>
                  <CardHeader className="text-center space-y-2">
                    <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                      <Mail className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-2xl font-bold">{t('reset_password.title')}</CardTitle>
                    <CardDescription>
                      {t('reset_password.subtitle')}
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="email">{t('reset_password.email')}</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder={t('reset_password.email_placeholder')}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          disabled={loading}
                          autoComplete="email"
                          autoFocus
                        />
                      </div>

                      {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-600 text-center">{error}</p>
                        </div>
                      )}

                      <Button
                        type="submit"
                        className="w-full"
                        disabled={loading || !email.trim()}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('reset_password.submitting')}
                          </>
                        ) : (
                          t('reset_password.submit')
                        )}
                      </Button>

                      <div className="text-center">
                        <Link
                          to="/login"
                          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <ArrowLeft size={16} />
                          {t('reset_password.back_to_login')}
                        </Link>
                      </div>
                    </form>
                  </CardContent>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
